import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";

export type DbDraftType = "post" | "comment";

export interface DbDraft {
  source: "db";
  draftType: DbDraftType;
  entityType: string;
  id: number;
  kind: string;
  status: "draft";
  title: string | null;
  content: string | null;
  body: string | null;
  properties: Record<string, unknown>;
  issue_id: number | null;
  commentable_type: string | null;
  commentable_id: number | null;
  parent_id: number | null;
  root_id: number | null;
  user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DraftListResponse {
  items: DbDraft[];
  total: number;
  limit: number;
  offset: number;
}

export interface DraftCountResponse {
  total: number;
  posts: number;
  comments: number;
}

export interface DraftListFilters {
  source?: DbDraftType;
  kind?: string;
  collectionType?: string;
  limit?: number;
  offset?: number;
}

export const draftQueryKeys = {
  all: () => ["drafts"] as const,
  count: () => ["drafts", "count"] as const,
  list: (filters: DraftListFilters = {}) => ["drafts", "list", filters] as const,
};

function buildDraftSearchParams(filters: DraftListFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set("limit", String(filters.limit ?? 20));
  params.set("offset", String(filters.offset ?? 0));
  if (filters.source) {
    params.set("source", filters.source);
  }
  if (filters.kind) {
    params.set("kind", filters.kind);
  }
  if (filters.collectionType) {
    params.set("collectionType", filters.collectionType);
  }
  return params;
}

export function useDraftCountQuery(enabled = true) {
  return useQuery<DraftCountResponse, Error>({
    queryKey: draftQueryKeys.count(),
    queryFn: async () => {
      const response = await api.get<DraftCountResponse>(apiRoutes.drafts.count);
      return response.data;
    },
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useDraftsQuery(filters: DraftListFilters = {}, enabled = true) {
  return useQuery<DraftListResponse, Error>({
    queryKey: draftQueryKeys.list(filters),
    queryFn: async () => {
      const params = buildDraftSearchParams(filters);
      const response = await api.get<DraftListResponse>(
        `${apiRoutes.drafts.root}?${params.toString()}`
      );
      return response.data;
    },
    enabled,
    staleTime: 15_000,
  });
}

export function useDeleteDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draft: { draftType: DbDraftType; entityType: string; id: number }) => {
      const entityType = draft.draftType === "comment" ? "comment" : draft.entityType;
      const response = await api.delete(apiRoutes.drafts.byEntity(entityType, draft.id));
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: draftQueryKeys.all() });
    },
  });
}
